// ─── Credit level metadata ────────────────────────────────────────────────────
export const CREDIT_META = {
  1: {
    label: "Essential",
    example: "Admin · Teller · Call Centre",
    color: "#94A3B8",
    bg: "rgba(100,116,139,0.12)",
    border: "rgba(100,116,139,0.28)",
  },
  2: {
    label: "Standard",
    example: "Sales Rep · IT Specialist",
    color: "#38BDF8",
    bg: "rgba(56,189,248,0.10)",
    border: "rgba(56,189,248,0.28)",
  },
  3: {
    label: "Specialist",
    example: "Financial Analyst · QA Engineer",
    color: "#A78BFA",
    bg: "rgba(167,139,250,0.10)",
    border: "rgba(167,139,250,0.28)",
  },
  4: {
    label: "Senior",
    example: "Developer · Finance Manager",
    color: "#FBBF24",
    bg: "rgba(251,191,36,0.10)",
    border: "rgba(251,191,36,0.28)",
  },
  5: {
    label: "Expert",
    example: "Full Stack · Data Scientist",
    color: "#F87171",
    bg: "rgba(248,113,113,0.10)",
    border: "rgba(248,113,113,0.28)",
  },
  6: {
    label: "Elite",
    example: "AI Engineer · ML Engineer",
    color: "#E879F9",
    bg: "rgba(232,121,249,0.10)",
    border: "rgba(232,121,249,0.28)",
  },
};

// ─── Credit packs ─────────────────────────────────────────────────────────────
export const PACKS = [
  {
    id: "starter",
    name: "Starter",
    credits: 10,
    best: "1–2 hires",
    usd: 100,
    ngn: 35000,
    usdPerC: 10,
    ngnPerC: 3500,
  },
  {
    id: "growth",
    name: "Growth",
    credits: 30,
    best: "4–6 hires",
    usd: 270,
    ngn: 90000,
    usdPerC: 9,
    ngnPerC: 3000,
  },
  {
    id: "scale",
    name: "Scale",
    credits: 75,
    best: "10–15 hires",
    usd: 600,
    ngn: 200000,
    usdPerC: 8,
    ngnPerC: 2667,
  },
];

export const PACK_FEATURES = [
  { text: "AI-powered candidate matching", ok: true },
  { text: "AI job post creation", ok: true },
  { text: "Top 5 candidate shortlist", ok: true },
  { text: "1 active job post at a time", ok: true },
  { text: "Credits roll over — never lost", ok: true },
  { text: "Packs stack — buy more any time", ok: true },
  { text: "Works alongside any annual plan", ok: true },
  { text: "Email support", ok: true },
  { text: "Team collaboration", ok: false },
  { text: "Advanced analytics", ok: false },
  { text: "API access", ok: false },
];

// ─── Annual plans ─────────────────────────────────────────────────────────────
export const PLANS = [
  {
    id: "starter",
    name: "Starter",
    accent: "#4F7EF7",
    popular: false,
    usd: 360,
    ngn: 120000,
    usdMo: 30,
    ngnMo: 10000,
    creditsPerMonth: 5,
    yearCredits: 60,
    usdPerC: 6.0,
    ngnPerC: 2000,
    topUpUsd: 7,
    topUpNgn: 2500,
    features: [
      "60 credits upfront — full year's allocation",
      "Unused credits roll over — never lost",
      "AI-powered matching",
      "AI job post creation",
      "Top 5 candidate shortlist",
      "2 active job posts",
      "Email support",
    ],
    notIncluded: ["Team collaboration", "Advanced analytics", "API access"],
  },
  {
    id: "growth",
    name: "Growth",
    accent: "#8B5CF6",
    popular: true,
    usd: 840,
    ngn: 280000,
    usdMo: 70,
    ngnMo: 23333,
    creditsPerMonth: 15,
    yearCredits: 180,
    usdPerC: 4.67,
    ngnPerC: 1556,
    topUpUsd: 6,
    topUpNgn: 2000,
    features: [
      "180 credits upfront — full year's allocation",
      "Unused credits roll over — never lost",
      "Enhanced AI matching",
      "AI job post creation",
      "Top 10 candidate shortlist",
      "5 active job posts",
      "Team collaboration (3 seats)",
      "Advanced analytics dashboard",
      "Priority support",
    ],
    notIncluded: ["API access", "Dedicated success manager"],
  },
  {
    id: "scale",
    name: "Scale",
    accent: "#F59E0B",
    popular: false,
    usd: 1920,
    ngn: 650000,
    usdMo: 160,
    ngnMo: 54167,
    creditsPerMonth: 40,
    yearCredits: 480,
    usdPerC: 4.0,
    ngnPerC: 1354,
    topUpUsd: 5,
    topUpNgn: 1700,
    features: [
      "480 credits upfront — full year's allocation",
      "Unused credits roll over — never lost",
      "Priority AI matching queue",
      "AI job post creation",
      "Unlimited candidate shortlists",
      "Unlimited active job posts",
      "Team collaboration (10 seats)",
      "AI Workforce Insights",
      "Dedicated success manager",
      "Full API access",
    ],
    notIncluded: [],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    accent: "#EC4899",
    popular: false,
    usd: null,
    ngn: null,
    usdMo: null,
    ngnMo: null,
    creditsPerMonth: null,
    yearCredits: null,
    usdPerC: null,
    ngnPerC: null,
    topUpUsd: null,
    topUpNgn: null,
    features: [
      "Custom credit volume",
      "Custom validity terms",
      "Multi-client subscriptions for agencies (roadmap)",
      "SLA-backed AI matching",
      "AI job post creation",
      "Unlimited candidate shortlists",
      "Unlimited active job posts",
      "Unlimited team seats",
      "HRIS & ATS integrations",
      "White-glove onboarding",
      "Engineering support + custom workflows",
    ],
    notIncluded: [],
  },
];

// ─── Role data ────────────────────────────────────────────────────────────────
export const ROLE_DATA = [
  {
    category: "Business & Finance",
    icon: "💼",
    subcategories: [
      {
        name: "Business Management",
        roles: [
          { title: "Business Analyst", credits: 3 },
          { title: "Project Manager", credits: 3 },
          { title: "Operations Manager", credits: 4 },
          { title: "Business Development Manager", credits: 4 },
          { title: "Product Manager", credits: 5 },
        ],
      },
      {
        name: "Finance",
        roles: [
          { title: "Audit Trainee", credits: 1 },
          { title: "Accountant", credits: 2 },
          { title: "Financial Analyst", credits: 3 },
          { title: "Finance Manager", credits: 4 },
          { title: "Wealth Advisor", credits: 4 },
          { title: "Portfolio Manager", credits: 5 },
        ],
      },
      {
        name: "HR & Administration",
        roles: [
          { title: "Administration Assistant", credits: 1 },
          { title: "Personal Assistant", credits: 1 },
          { title: "Administrative Secretary", credits: 1 },
          { title: "Executive Assistant", credits: 2 },
          { title: "Recruiter", credits: 2 },
          { title: "HR Officer / HR Manager", credits: 3 },
        ],
      },
    ],
  },
  {
    category: "Technology & Digital",
    icon: "💻",
    subcategories: [
      {
        name: "Software Engineering",
        roles: [
          { title: "Frontend Developer", credits: 4 },
          { title: "Backend Developer", credits: 4 },
          { title: "Mobile Developer", credits: 4 },
          { title: "Software Engineer", credits: 4 },
          { title: "Full Stack Developer", credits: 5 },
        ],
      },
      {
        name: "AI & Data",
        roles: [
          { title: "Data Analyst", credits: 3 },
          { title: "Data Scientist", credits: 5 },
          { title: "Data Engineer", credits: 5 },
          { title: "AI Engineer", credits: 6 },
          { title: "Machine Learning Engineer", credits: 6 },
        ],
      },
      {
        name: "Cloud & Infrastructure",
        roles: [
          { title: "Networking Hardware Specialist", credits: 3 },
          { title: "DevOps Engineer", credits: 5 },
          { title: "Cloud Engineer", credits: 5 },
          { title: "Site Reliability Engineer", credits: 5, future: true },
        ],
      },
      {
        name: "Security",
        roles: [
          { title: "Information Security Specialist", credits: 4 },
          { title: "Cybersecurity Specialist", credits: 5 },
        ],
      },
      {
        name: "Quality Assurance",
        roles: [
          { title: "QA Engineer", credits: 3 },
          { title: "Lead QA Engineer", credits: 4 },
          { title: "Test Automation Engineer", credits: 4, future: true },
        ],
      },
      {
        name: "IT Support",
        roles: [
          { title: "IT Specialist", credits: 2 },
          { title: "Technical Support Engineer", credits: 2, future: true },
          { title: "Systems Administrator", credits: 3, future: true },
        ],
      },
    ],
  },
  {
    category: "Creative & Design",
    icon: "🎨",
    subcategories: [
      {
        name: "Product & UX",
        roles: [
          { title: "UI/UX Designer", credits: 3 },
          { title: "Product Designer", credits: 4 },
        ],
      },
      {
        name: "Marketing",
        roles: [
          { title: "Digital Marketer", credits: 2 },
          { title: "Marketing Manager", credits: 3 },
          { title: "Marketing & Communications Manager", credits: 4 },
        ],
      },
      {
        name: "Creative",
        roles: [
          { title: "Writer", credits: 2 },
          { title: "Copywriter", credits: 2, future: true },
          { title: "Content Creator", credits: 1, future: true },
          { title: "Graphic Designer", credits: 2, future: true },
          { title: "Brand Designer", credits: 3, future: true },
          { title: "Motion Designer", credits: 3, future: true },
          { title: "Video Editor", credits: 2, future: true },
          { title: "Social Media Manager", credits: 2, future: true },
        ],
      },
      { name: "Architecture", roles: [{ title: "Architect", credits: 4 }] },
    ],
  },
  {
    category: "Sales & Operations",
    icon: "🚚",
    subcategories: [
      {
        name: "Sales",
        roles: [
          { title: "Sales Representative", credits: 2 },
          { title: "Sales Associate (Banking)", credits: 2 },
          { title: "Sales Engineer", credits: 3 },
          { title: "Real Estate Sales Representative", credits: 2 },
        ],
      },
      {
        name: "Customer Support",
        roles: [
          { title: "Customer Service Representative", credits: 1 },
          { title: "Call Centre Agent", credits: 1 },
          { title: "Call Centre Supervisor", credits: 2 },
          { title: "Call Centre Manager", credits: 3 },
        ],
      },
      {
        name: "Banking Operations",
        roles: [
          { title: "Bank Teller", credits: 1 },
          { title: "Fund Transfer Officer", credits: 2 },
          { title: "Banking Customer Service", credits: 1 },
        ],
      },
      {
        name: "Logistics & Field Operations",
        roles: [
          { title: "Dispatch Rider", credits: 1 },
          { title: "Site Acquisition Agent", credits: 2 },
          { title: "Facility Manager", credits: 3 },
        ],
      },
      {
        name: "Industrial & Technical Operations",
        roles: [
          { title: "Mechanical Technician", credits: 2 },
          { title: "Workshop Lead Technician", credits: 3 },
        ],
      },
      {
        name: "Retail Operations",
        roles: [{ title: "Supermarket Manager", credits: 3 }],
      },
    ],
  },
];

export const ALL_ROLES_FLAT = ROLE_DATA.reduce((acc, cat) => {
  cat.subcategories.forEach((sub) =>
    sub.roles.forEach((role) => {
      if (!role.future)
        acc.push({
          ...role,
          category: cat.category,
          subcategory: sub.name,
          icon: cat.icon,
        });
    }),
  );
  return acc;
}, []);

/**
 * FAQ content, ported verbatim from the original's inline `FAQS` array
 * inside `FAQSection`. Extracted into a function since three answers
 * interpolate currency-dependent ranges (`packRange`/`topUpRange`).
 */
export function getFaqs(isNGN) {
  const topUpRange = isNGN ? "₦1,700–₦2,500" : "$5–7";
  const packRange = isNGN ? "₦2,667–₦3,500" : "$8–10";

  return [
    {
      q: "What exactly is a credit?",
      a: "Credits are what you spend to hire on AwaSource. Every role has a credit cost from 1C to 6C based on seniority and scarcity — an Admin Assistant is 1C, a Marketing Manager 3C, a Machine Learning Engineer 6C. See the Role Credits tab for the full list.",
    },
    {
      q: "Do my credits expire?",
      a: "No. Unused credits roll over indefinitely and are never deleted. You do need an active validity window to spend them — if your subscription lapses, credits are frozen (not lost) and unlock the moment you reactivate.",
    },
    {
      q: "What's the difference between a credit pack and an annual plan?",
      a: `Packs are one-time, pay-as-you-go top-ups at ${packRange}/credit with no commitment. Annual plans are a single upfront payment that issues your full year of credits immediately at a much lower effective rate — and unlock team collaboration, analytics, and priority support.`,
    },
    {
      q: "How does the 1-year validity work?",
      a: "Every plan purchase grants 12 months of platform access from the purchase date. Buying a new plan resets the clock from the new purchase date — validity doesn't stack, so two plans bought back-to-back don't add up to 24 months.",
    },
    {
      q: "What happens if my plan lapses?",
      a: "Your credits are frozen, not lost. They stay in your account and become spendable again as soon as you reactivate with any plan or pack.",
    },
    {
      q: "Do top-up packs extend my validity?",
      a: "Only when less than 1 month remains on your current window — then a top-up adds 1 month of access. If your yearly plan is still active, a top-up simply adds credits with no validity change.",
    },
    {
      q: "Is there any monthly billing?",
      a: "No. Nothing on AwaSource bills monthly. Packs are one-time purchases, and annual plans are a single upfront payment that covers 12 months.",
    },
    {
      q: "Can I upgrade or downgrade my plan?",
      a: "You can upgrade at any time. Downgrades aren't available mid-plan — your current plan runs its full term, and you're free to choose a lower plan at renewal.",
    },
    {
      q: "What if I run out of credits mid-year?",
      a: `Top up at your plan's reduced rate (${topUpRange}/credit — below pack rates). Top-up credits stack on your existing balance and are never lost.`,
    },
    {
      q: "How does the 30-day rematch guarantee work?",
      a: "If a hire leaves within their first 30 days, we rematch the role at 50% of its original credit cost. It applies to every pack and every plan.",
    },
    {
      q: "Can I get a refund?",
      a: "Yes — any pack or plan is fully refundable within 14 days of purchase if you haven't spent any credits, paid back to your original payment method in the original currency. Once credits are spent or 14 days pass, purchases are non-refundable — your value is protected instead by credits that never expire, the freeze-on-lapse rule, and the 30-day rematch guarantee. And if we can't deliver a qualified shortlist for a role you post, those credits are automatically returned to your balance.",
    },
    {
      q: "Can I pay in naira?",
      a: "Yes. Nigerian-registered companies pay local ₦ prices via Paystack; international clients pay in USD. Use the currency toggle above to compare.",
    },
    {
      q: "I'm a recruiting agency hiring for multiple clients — what should I use?",
      a: "Contact sales about Enterprise. Multi-client subscriptions — separate credit balances per client company under one agency account — are on our roadmap under the Enterprise plan.",
    },
    {
      q: "Which option should I choose?",
      a: "If you'll hire fewer than ~6 people this year, start with a pack. If you'd buy packs 4–5+ times a year, an annual plan already costs less — the Credit Calculator tab compares both for your exact roles.",
    },
  ];
}
